import React, { FC, useCallback, useEffect, useState } from 'react';
import axios from '../../../utils/axios';
import MaterialTable from '@material-table/core';

import { Box, Checkbox, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { RouteComponentProps } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Button, LoaderOverlay, TextField } from '../../../components/library';
import styledComponents from '../../../styledComponents';
import { useInventory } from '../../catalog/utils/useInventory';
import { UpcSearchModal } from '../../service/UpcSearchModal';
import { Inventory, WarehouseBin } from '../../../services/inventory/types';
import { API_URL } from '../../../constants';
import { WarehouseBinAutocomplete } from '../../../components/inventory/WarehouseBinAutocomplete';
import { AutoCloseAlert, useAutoCloseAlert } from '../../../components/library/AutoCloseAlert';
import { AlertTitle } from '@mui/material';
import { ProductVariant } from '../../../services/catalog/types';
import { FormatMoney } from '../../../utils/helper';

export const BIKE_PART_OUT_ROUTE = '/inventory/bike-part-out';

const WAREHOUSE_ZONE_ID = 8;
const BIKE_PART_OUT_REASON_CODE = 'bike-part-out';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      flexDirection: 'row',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    inputContainer: {
      width: '38%',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
      display: 'flex',
      flexDirection: 'column',
    },
    summaryContainer: {
      marginTop: theme.spacing(3),
      width: '60%',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
  }),
);

const { PageWrapper } = styledComponents;

const InlineTextField = styled(TextField)`
  display: inline-block;
`;

const CheckboxWrapper = styled.label`
  display: inline-block;
  cursor: pointer;
`;

interface RelocationRow {
  id: number;
  productVariant: ProductVariant;
  warehouseBin: WarehouseBin;
  quantityAdjusted: number;
  amount: number | null;
}

const BikePartOut: FC<Partial<RouteComponentProps>> = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);

  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const {
    autoCloseAlertOpen: successAutoCloseAlertOpen,
    handleAutoCloseAlertOpen: handleSuccessAutoCloseAlertOpen,
    handleAutoCloseAlertClose: handleSuccessAutoCloseAlertClose,
  } = useAutoCloseAlert();
  const onSuccessAutoAlertClose = useCallback(() => {
    setSuccessMessage(undefined);
    handleSuccessAutoCloseAlertClose();
  }, [setSuccessMessage, handleSuccessAutoCloseAlertClose]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    autoCloseAlertOpen: errorAutoCloseAlertOpen,
    handleAutoCloseAlertOpen: handleErrorAutoCloseAlertOpen,
    handleAutoCloseAlertClose: handleErrorAutoCloseAlertClose,
  } = useAutoCloseAlert();
  const onErrorAutoAlertClose = useCallback(() => {
    setErrorMessage(undefined);
    handleErrorAutoCloseAlertClose();
  }, [setErrorMessage, handleErrorAutoCloseAlertClose]);

  const [inventoryAdjusmentNote, setInventoryAdjusmentNote] = useState<string>('');

  const [sourceSku, setSourceSku] = useState<string>('');
  const [sourceProductVariant, setSourceProductVariant] = useState<ProductVariant | undefined>();
  const [sourceInventoryRecord, setSourceInventoryRecord] = useState<Inventory | undefined>();
  const [sourceSkuConfirmed, setSourceSkuConfirmed] = useState<boolean>(false);
  const [sourceSkuError, setSourceSkuError] = useState<string | undefined>(undefined);
  const [sourceSkuLoading, setSourceSkuLoading] = useState<boolean>(false);
  const [sourceCost, setSourceCost] = useState<string>('');

  const [binLocationOptions, setBinLocationOptions] = useState<WarehouseBin[]>([]);
  const [binTypeahead, setBinTypeahead] = useState<string>('');
  const [addItemBinLocked, setAddItemBinLocked] = useState<boolean>(false);

  const [addItemSku, setAddItemSku] = useState<string>('');
  const [productVariant, setProductVariant] = useState<ProductVariant | undefined>();
  const [currentInvDetails, setCurrentInvDetails] = useState<Inventory[]>([]);
  const [addItemWarehouseBin, setAddItemWarehouseBin] = useState<WarehouseBin | undefined>(
    undefined,
  );
  const [addItemAdjustmentQuantity, setAddItemAdjustmentQuantity] = useState<string>('');
  const [addItemAmount, setAddItemAmount] = useState<string>('');

  const [itemAddError, setItemAddError] = useState<string | undefined>(undefined);
  const {
    autoCloseAlertOpen: itemErrorAutoCloseAlertOpen,
    handleAutoCloseAlertOpen: handleItemErrorAutoCloseAlertOpen,
    handleAutoCloseAlertClose: handleItemErrorAutoCloseAlertClose,
  } = useAutoCloseAlert();

  const onItemErrorAutoAlertClose = useCallback(() => {
    setItemAddError(undefined);
    handleItemErrorAutoCloseAlertClose();
  }, [setItemAddError, handleItemErrorAutoCloseAlertClose]);

  const [upcModalOpen, setUpcModalOpen] = useState(false);

  const [relocationRows, setRelocationRows] = useState<RelocationRow[]>([]);

  const [adjustmentReasonId, setAdjustmentReasonId] = useState<number | undefined>(undefined);
  const [configError, setConfigError] = useState<string | undefined>(undefined);
  const [configLoading, setConfigLoading] = useState<boolean>(true);

  useEffect(() => {
    const validateConfig = async () => {
      try {
        let reasons: any[] = [];
        let zones: any[] = [];

        await axios
          .get(`${API_URL}/inventory/inventoryAdjustmentReasons?includeInactive=true`)
          .then(res => {
            reasons = res?.data ?? [];
          });
        await axios.get(`${API_URL}/inventory/zones`).then(res => {
          zones = res?.data ?? [];
        });

        const reason = reasons.find(
          (r: { id: number; code: string }) => r.code === BIKE_PART_OUT_REASON_CODE,
        );
        if (!reason) {
          setConfigError(
            `Adjustment reason "${BIKE_PART_OUT_REASON_CODE}" not found. Please contact an administrator.`,
          );
          return;
        }

        const zone = zones.find(
          (z: { id: number; inventoryType: string; active: number }) => z.id === WAREHOUSE_ZONE_ID,
        );
        if (!zone) {
          setConfigError(
            `Warehouse zone ${WAREHOUSE_ZONE_ID} not found. Please contact an administrator.`,
          );
          return;
        }
        if (zone.inventoryType !== 'operations') {
          setConfigError(
            `Warehouse zone ${WAREHOUSE_ZONE_ID} is not an operations zone. Please contact an administrator.`,
          );
          return;
        }
        if (!zone.active) {
          setConfigError(
            `Warehouse zone ${WAREHOUSE_ZONE_ID} is not active. Please contact an administrator.`,
          );
          return;
        }

        setAdjustmentReasonId(reason.id);
      } catch (err) {
        setConfigError('Failed to load configuration. Please refresh and try again.');
      } finally {
        setConfigLoading(false);
      }
    };
    validateConfig();
  }, []);

  const { warehouseBinsForZone, error: relocateHookError } = useInventory({
    typeaheadVal: binTypeahead,
    zoneId: WAREHOUSE_ZONE_ID,
  });

  const runningTotal = relocationRows.reduce(
    (sum, r) => sum + (r.amount || 0) * r.quantityAdjusted,
    0,
  );

  useEffect(() => {
    if (productVariant) {
      const binLocations = warehouseBinsForZone;
      setBinLocationOptions(binLocations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productVariant, warehouseBinsForZone]);

  const lookupSourceSku = async () => {
    if (!sourceSku.trim()) return;
    setSourceSkuLoading(true);
    setSourceSkuError(undefined);
    setSourceProductVariant(undefined);
    setSourceInventoryRecord(undefined);
    try {
      const res = await axios.get(`${API_URL}/inventory/skus/${sourceSku.trim()}`);
      const variant = res?.data;
      if (!variant) {
        setSourceSkuError('SKU not found');
        return;
      }
      const inv = variant.warehouseInventories?.find(
        (i: Inventory) => i.warehouseBin?.warehouseZoneId === WAREHOUSE_ZONE_ID && i.quantity > 0,
      );
      if (!inv) {
        setSourceSkuError('No inventory found in Thornton > Operations');
        return;
      }
      setSourceProductVariant(variant);
      setSourceInventoryRecord(inv);
      setSourceCost(variant.costUsd != null ? String(variant.costUsd) : '');
    } catch (err) {
      setSourceSkuError(
        err?.response?.data?.message ?? err?.message ?? 'An unknown error occurred',
      );
    } finally {
      setSourceSkuLoading(false);
    }
  };

  const confirmSourceSku = () => {
    if (
      !sourceProductVariant ||
      !sourceInventoryRecord?.warehouseBin ||
      !sourceProductVariant?.costUsd
    )
      return;
    setRelocationRows([
      {
        id: 0,
        productVariant: sourceProductVariant,
        warehouseBin: sourceInventoryRecord.warehouseBin,
        quantityAdjusted: -1,
        amount: sourceCost ? parseFloat(sourceCost) : null,
      },
    ]);
    setSourceSkuConfirmed(true);
  };

  const handleSkuByUpc = (value: string) => {
    setUpcModalOpen(false);
    setAddItemSku(value);
  };
  const skuInputRef = React.useRef<HTMLInputElement>(null);

  const getCurrentSkuInventoryDetails = async () => {
    setItemAddError(undefined);
    handleItemErrorAutoCloseAlertClose();
    if (addItemSku) {
      try {
        await axios.get(`${API_URL}/inventory/skus/${addItemSku.trim()}`).then(res => {
          const productVariant = res?.data;
          if (!productVariant) {
            setItemAddError('SKU not found');
            handleItemErrorAutoCloseAlertOpen();
            return;
          }
          setProductVariant(productVariant);
          const productInventories = productVariant.warehouseInventories.filter(
            (i: Inventory) => !!i.quantity,
          );
          setCurrentInvDetails(productInventories);
        });
      } catch (err) {
        const errMsg = err?.response?.data?.message
          ? err.response.data.message
          : err?.message
          ? err?.message
          : 'An unknown error occurred';
        setItemAddError(errMsg);
        handleItemErrorAutoCloseAlertOpen();
      }
    }
  };

  const deleteItem = (xferObj: { id: number }): Promise<void> => {
    const { id } = xferObj;
    const xferArr = relocationRows;
    const newArr = xferArr
      .filter((x: any) => x.id !== id)
      .map((x: any, idx: number) => ({ ...x, id: idx }));
    setRelocationRows(newArr);
    return Promise.resolve();
  };

  const validateAddItem = (selectedInventoryDetails?: Inventory) => {
    if (!addItemSku || !addItemWarehouseBin || !addItemAdjustmentQuantity) {
      return 'Please fill out all fields';
    }
    if (
      relocationRows.find(
        row =>
          row.productVariant.sku === addItemSku && row.warehouseBin.id === addItemWarehouseBin.id,
      )
    ) {
      return `Item ${addItemSku} in bin ${addItemWarehouseBin.code} is already in the list`;
    }

    const addItemQuantityNumber = parseInt(addItemAdjustmentQuantity, 10);
    if (
      selectedInventoryDetails &&
      addItemQuantityNumber < 0 &&
      selectedInventoryDetails.quantity < Math.abs(addItemQuantityNumber)
    ) {
      return `You're adjustment will take the inventory of sku ${addItemSku} quantity which has ${selectedInventoryDetails.quantity} to less than zero with your adjustment ${addItemQuantityNumber}.`;
    }
    if (addItemWarehouseBin.warehouseZoneId !== WAREHOUSE_ZONE_ID) {
      return `Bin ${addItemWarehouseBin.code} is not in the correct warehouse zone`;
    }
    return undefined;
  };

  const clearPageState = () => {
    setRelocationRows([]);
    setInventoryAdjusmentNote('');
    setAddItemSku('');
    setAddItemWarehouseBin(undefined);
    setAddItemAdjustmentQuantity('');
    setAddItemAmount('');
    setProductVariant(undefined);
    setSourceSku('');
    setSourceProductVariant(undefined);
    setSourceInventoryRecord(undefined);
    setSourceSkuConfirmed(false);
    setSourceSkuError(undefined);
    setSourceCost('');
    handleErrorAutoCloseAlertOpen();
  };

  const clearItemForm = () => {
    setAddItemSku('');
    setAddItemAdjustmentQuantity('');
    setAddItemAmount('');
    if (!addItemBinLocked) {
      setAddItemWarehouseBin(undefined);
    }
    setProductVariant(undefined);
  };

  const addItem = () => {
    if (!productVariant) {
      setItemAddError('SKU not found');
      handleItemErrorAutoCloseAlertOpen();
      return;
    }
    const selectedInventoryDetails = currentInvDetails.find(
      inv => inv.warehouseBinId === addItemWarehouseBin?.id,
    );
    const errorMessage = validateAddItem(selectedInventoryDetails);
    if (errorMessage) {
      setItemAddError(errorMessage);
      handleItemErrorAutoCloseAlertOpen();
      return;
    }
    const parsedAmount = addItemAmount ? parseFloat(addItemAmount) : null;
    const newItems: RelocationRow[] = [
      ...relocationRows,
      {
        id: relocationRows.length,
        productVariant: productVariant,
        warehouseBin: addItemWarehouseBin as WarehouseBin,
        quantityAdjusted: parseInt(addItemAdjustmentQuantity, 10),
        amount: parsedAmount && !isNaN(parsedAmount) ? parsedAmount : null,
      },
    ];
    setRelocationRows(newItems as RelocationRow[]);
    clearItemForm();
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setLoading(true);
    if (!inventoryAdjusmentNote) {
      setErrorMessage('Please enter a note');
      handleErrorAutoCloseAlertOpen();
      setLoading(false);
      return;
    }
    try {
      const inventoryAdjustmentItems = relocationRows.map(row => ({
        sku: row.productVariant.sku,
        warehouseBinId: row.warehouseBin?.id,
        quantityAdjusted: row.quantityAdjusted,
        amount: row.amount ?? null,
      }));
      await axios.post(`${API_URL}/inventory/adjustment`, {
        warehouseZoneId: WAREHOUSE_ZONE_ID,
        adjustmentReasonId,
        notes: inventoryAdjusmentNote,
        inventoryAdjustmentItems,
      });
      setSuccessMessage('Bike Part-Out Adjustment Created');
      handleSuccessAutoCloseAlertOpen();
      clearPageState();
    } catch (err) {
      const error = err?.response?.data?.message
        ? err.response.data.message
        : err?.message
        ? err?.message
        : 'An unknown error occurred';
      setErrorMessage(error);
      handleErrorAutoCloseAlertOpen();
    } finally {
      setLoading(false);
    }
  };

  const theme = useTheme();
  return (
    <PageWrapper style={{ overflow: 'hidden' }}>
      <LoaderOverlay loading={loading} />
      <Box
        style={{
          width: '100%',
          maxWidth: '1500px',
          marginTop: theme.spacing(8),
        }}
      >
        <LoaderOverlay loading={loading} />
        <Typography variant="h4" gutterBottom>
          Bike Part-Out
        </Typography>
        {configLoading && <LoaderOverlay loading />}
        {configError && (
          <Box style={{ marginTop: theme.spacing(2) }}>
            <AutoCloseAlert
              open
              severity="error"
              handleClose={() => {
                console.error('Config error:', configError);
              }}
            >
              <AlertTitle>Configuration Error</AlertTitle>
              {configError}
            </AutoCloseAlert>
          </Box>
        )}
        {!configLoading && !configError && (
          <Box
            className={classes.formContainer}
            style={{
              width: '100%',
              marginTop: theme.spacing(3),
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box className={classes.inputContainer}>
              <Box
                className="source-input"
                style={{
                  marginTop: theme.spacing(3),
                  padding: theme.spacing(3),
                  border: `5px solid ${theme.palette.divider}`,
                }}
              >
                <Box style={{ marginTop: theme.spacing(3) }}>
                  <InlineTextField
                    id="sourceSku"
                    name="sourceSku"
                    value={sourceSku}
                    onChange={e => setSourceSku(e.target.value)}
                    onBlur={lookupSourceSku}
                    placeholder="Enter Source SKU"
                    label="Source SKU"
                    variant="outlined"
                    disabled={sourceSkuConfirmed}
                  />
                </Box>
                {sourceInventoryRecord && sourceProductVariant && (
                  <Box style={{ marginTop: theme.spacing(2) }}>
                    {sourceSkuConfirmed ? (
                      <Typography variant="body2">
                        {`SKU: ${sourceSku}  |  Bin: ${
                          sourceInventoryRecord.warehouseBin?.code
                        }  |  Cost: $${sourceCost || 'N/A'}`}
                      </Typography>
                    ) : (
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(2),
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography variant="body2">
                          {`SKU: ${sourceSku}  |  Bin: ${sourceInventoryRecord.warehouseBin?.code}`}
                        </Typography>
                        <InlineTextField
                          id="sourceCost"
                          name="sourceCost"
                          type="number"
                          value={sourceCost}
                          onChange={e => setSourceCost(e.target.value)}
                          placeholder="Enter Cost"
                          label="Cost"
                          variant="outlined"
                        />
                        <Button
                          ordinality="primary"
                          disabled={sourceSkuLoading || !sourceCost}
                          style={{ marginTop: theme.spacing(1) }}
                          onClick={confirmSourceSku}
                        >
                          Add Item
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                {sourceSkuError && (
                  <Typography variant="body2" style={{ color: 'red', marginTop: theme.spacing(1) }}>
                    {sourceSkuError}
                  </Typography>
                )}
                <Box style={{ marginTop: theme.spacing(3) }}>
                  <InlineTextField
                    id="inventoryAdjusmentNote"
                    name="inventoryAdjusmentNote"
                    value={inventoryAdjusmentNote || ''}
                    onChange={e => setInventoryAdjusmentNote(e.target.value)}
                    placeholder="Enter a note"
                    label="Notes"
                    variant="outlined"
                  />
                </Box>
              </Box>
              {sourceSkuConfirmed && (
                <Box
                  className="item-input"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginTop: theme.spacing(3),
                    padding: theme.spacing(3),
                    border: `5px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <InlineTextField
                      tabIndex={0}
                      autoFocus
                      id="SkuRelocateSKU"
                      name="sku"
                      value={addItemSku}
                      onChange={e => setAddItemSku(e.target.value)}
                      placeholder="Enter SKU"
                      label="SKU"
                      variant="outlined"
                      inputRef={skuInputRef}
                      onBlur={getCurrentSkuInventoryDetails}
                    />
                    <span style={{ padding: '0 1rem' }}>or</span>
                    <Button
                      onClick={() => setUpcModalOpen(true)}
                      ordinality="secondary"
                      tabIndex={-1}
                    >
                      Search by UPC
                    </Button>
                    {upcModalOpen && (
                      <UpcSearchModal
                        open={upcModalOpen}
                        handleClose={() => setUpcModalOpen(false)}
                        setSku={handleSkuByUpc}
                      />
                    )}
                  </Box>
                  <Box
                    style={{
                      marginTop: theme.spacing(3),
                      gap: theme.spacing(2),
                      display: 'flex',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    <Box style={{ width: '50%' }}>
                      <WarehouseBinAutocomplete
                        style={{ width: '100%' }}
                        id="InventoryAdjusmentItemBin"
                        selectedBin={addItemWarehouseBin}
                        label="Warehouse Bin"
                        placeholder="Select the Bin"
                        options={binLocationOptions.filter(
                          bin => bin.warehouseZoneId === WAREHOUSE_ZONE_ID,
                        )}
                        typeaheadVal={binTypeahead}
                        onTypeaheadChange={setBinTypeahead}
                        onBinChange={e => {
                          setAddItemWarehouseBin((e as unknown) as WarehouseBin);
                          skuInputRef.current?.focus();
                        }}
                      />
                      <CheckboxWrapper>
                        <Checkbox
                          checked={addItemBinLocked}
                          onChange={() => setAddItemBinLocked(!addItemBinLocked)}
                          tabIndex={-1}
                        />
                        Lock Bin
                      </CheckboxWrapper>
                    </Box>
                  </Box>
                  <Box
                    style={{
                      marginTop: theme.spacing(1),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      width: '45%',
                    }}
                  >
                    <InlineTextField
                      id="InventoryAdjusmentItemQuantity"
                      name="quantity"
                      type="number"
                      value={addItemAdjustmentQuantity}
                      onChange={e => setAddItemAdjustmentQuantity(e.target.value)}
                      placeholder="Enter Quantity"
                      label="Quantity"
                      variant="outlined"
                    />
                    <InlineTextField
                      id="InventoryAdjusmentItemAmount"
                      name="amount"
                      type="number"
                      value={addItemAmount}
                      onChange={e => setAddItemAmount(e.target.value)}
                      placeholder="Enter Amount"
                      label="Amount"
                      variant="outlined"
                      style={{ marginTop: theme.spacing(1) }}
                    />
                  </Box>
                  <Box style={{ marginTop: theme.spacing(1) }}>
                    <Button
                      size="large"
                      type="submit"
                      ordinality="primary"
                      disabled={
                        loading ||
                        !addItemSku ||
                        !addItemWarehouseBin ||
                        !addItemAdjustmentQuantity ||
                        !!relocateHookError
                      }
                      style={{ width: '100%', marginTop: '1rem' }}
                      onClick={addItem}
                    >
                      Add Item
                    </Button>
                  </Box>
                  {itemAddError && (
                    <AutoCloseAlert
                      open={itemErrorAutoCloseAlertOpen}
                      handleClose={onItemErrorAutoAlertClose}
                      autoHideDuration={15000}
                      severity="error"
                      style={{ width: '100%', marginTop: '1rem' }}
                    >
                      <AlertTitle>Error</AlertTitle>
                      {itemAddError}
                    </AutoCloseAlert>
                  )}
                </Box>
              )}
              {successMessage && (
                <AutoCloseAlert
                  open={successAutoCloseAlertOpen}
                  handleClose={onSuccessAutoAlertClose}
                  severity="success"
                  autoHideDuration={8000}
                  style={{ width: '100%', marginTop: '1rem' }}
                >
                  <AlertTitle>Success</AlertTitle>
                  {successMessage}
                </AutoCloseAlert>
              )}
              {errorMessage && (
                <AutoCloseAlert
                  open={errorAutoCloseAlertOpen}
                  handleClose={onErrorAutoAlertClose}
                  autoHideDuration={15000}
                  severity="error"
                  style={{ width: '100%', marginTop: '1rem' }}
                >
                  <AlertTitle>Error</AlertTitle>
                  {errorMessage}
                </AutoCloseAlert>
              )}
            </Box>
            <Box className={classes.summaryContainer}>
              <Box
                style={{
                  marginTop: theme.spacing(1),
                  padding: theme.spacing(1),
                  textAlign: 'right',
                }}
              >
                <Typography
                  variant="body1"
                  style={{
                    color: parseInt(runningTotal.toFixed(2)) === 0 ? 'green' : 'red',
                    fontWeight: 'bold',
                  }}
                >
                  {!!relocationRows.length &&
                    (parseInt(runningTotal.toFixed(2)) === 0
                      ? `Totals Match`
                      : `Running Total: ${FormatMoney(runningTotal)}`)}
                </Typography>
              </Box>
              <MaterialTable
                columns={[
                  { title: 'Row', field: 'id', editable: 'never' },
                  {
                    title: 'Sku',
                    field: 'sku',
                    editable: 'always',
                  },
                  {
                    title: 'Warehouse Bin',
                    field: 'bin',
                    editable: 'always',
                  },
                  {
                    title: 'Adjustment Quantity',
                    field: 'quantityToAdjust',
                    editable: 'always',
                  },
                  {
                    title: 'Amount',
                    field: 'amount',
                    editable: 'never',
                    render: (rowData: any) => {
                      if (
                        rowData.amount !== null &&
                        rowData.amount !== undefined &&
                        !isNaN(rowData.amount)
                      ) {
                        return FormatMoney(rowData.amount);
                      }
                      return 'N/A';
                    },
                  },
                ]}
                editable={{
                  isDeletable: undefined,
                  isEditable: undefined,
                  onRowAdd: undefined,
                  onRowUpdate: undefined,
                  onRowDelete: (oldData: any) => deleteItem(oldData),
                }}
                title="Adjustment Items"
                data={relocationRows.map(row => ({
                  id: row.id,
                  bin: row.warehouseBin.code,
                  sku: row.productVariant.sku,
                  quantityToAdjust: row.quantityAdjusted,
                  amount: row.amount,
                }))}
                options={{ paging: false, sorting: false, search: false }}
              />
              <Button
                size="large"
                type="submit"
                ordinality="primary"
                disabled={
                  !!!relocationRows.length || loading || parseFloat(runningTotal.toFixed(2)) !== 0
                }
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={handleSubmit}
              >
                Create Bike Part-Out
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </PageWrapper>
  );
};

export default BikePartOut;
